import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { completeFirstLogin, logout } from "../redux/auth/authSlice";
import { putApi } from "../api/api_instance";
import {
  Eye,
  EyeOff,
  Lock,
  ShieldCheck,
  Info,
  X,
  Check,
  User,
} from "lucide-react";
import PopupCard from "../pages/Auth/PopupCard";
import Logo from "../assets/svgs/sidebarlogo.svg";
import ResetImg from "../assets/svgs/resetpassword1.svg";
import FirstImg from "../assets/svgs/firsticon.svg";
import SecondImg from "../assets/svgs/second.svg";
import ThirdImg from "../assets/svgs/third.svg";
import { useNavigate } from "react-router-dom";

export default function ResetPasswordModal() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, employeeLogin } = useSelector((state) => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const [showRetypePassword, setShowRetypePassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [resetData, setResetData] = useState({
    email: user?.email || "",
    currentPassword: user?.tempPassword || "",
    newPassword: "",
    newPasswordRetype: "",
  });

  // Automatically sync state if user changes/loads
  React.useEffect(() => {
    if (user) {
      setResetData((prev) => ({
        ...prev,
        email: user.email || "",
        currentPassword: user.tempPassword || "",
      }));
    }
  }, [user]);

  // Auto-close success popup after 2 seconds
  React.useEffect(() => {
    if (showSuccessPopup) {
      const timer = setTimeout(() => {
        const hasDashboardPerm =
          (user?.subAdmin?.isSubAdmin &&
            user?.subAdmin?.permissions?.some(
              (p) => p.module === "dashboard" || p.module === "dashbaord",
            )) ||
          (user?.managementView?.isManagementView &&
            user?.managementView?.permissions?.some(
              (p) => p.module === "dashboard" || p.module === "dashbaord",
            )) ||
          user?.managementView?.isManagementView;

        if (hasDashboardPerm) {
          navigate("/organization/dashboard", {
            state: { showWelcomeModal: true },
          });
        } else {
          navigate("/employee/home", { state: { showWelcomeModal: true } });
        }

        // Then complete first login after a small delay to prevent login page flash
        setTimeout(() => {
          setShowSuccessPopup(false);
          dispatch(completeFirstLogin());
        }, 100);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessPopup, dispatch, navigate, user]);

  // Auto-close error popup after 2 seconds
  React.useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  if (!employeeLogin || !user?.isFirstLogin) {
    return null;
  }

  const showTempPasswordInput = !user?.tempPassword;

  // Password Strength Checklist Criteria
  const criteria = {
    length: resetData.newPassword.length >= 8,
    lowercase: /[a-z]/.test(resetData.newPassword),
    uppercase: /[A-Z]/.test(resetData.newPassword),
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(
      resetData.newPassword,
    ),
    number: /[0-9]/.test(resetData.newPassword),
  };

  const strengthScore = Object.values(criteria).filter(Boolean).length;

  const getStrengthLabel = () => {
    if (resetData.newPassword.length === 0) {
      return {
        text: "None",
        color: "text-gray-400",
        bars: 0,
        barColor: "bg-gray-200",
      };
    }
    if (strengthScore <= 2) {
      return {
        text: "Weak",
        color: "text-red-600",
        bars: 1,
        barColor: "bg-[#06513D]",
      };
    }
    if (strengthScore <= 4) {
      return {
        text: "Medium",
        color: "text-amber-500",
        bars: 3,
        barColor: "bg-[#06513D]",
      };
    }
    return {
      text: "Strong",
      color: "text-emerald-600",
      bars: 4,
      barColor: "bg-[#06513D]",
    };
  };

  const strength = getStrengthLabel();

  const handleClose = () => {
    dispatch(logout());
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setResetData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !resetData.email ||
      // !resetData.currentPassword ||
      !resetData.newPassword ||
      !resetData.newPasswordRetype
    ) {
      setErrorMessage("Please fill in all fields.");
      return;
    }

    if (
      resetData.email.trim().toLowerCase() !== user.email.trim().toLowerCase()
    ) {
      setErrorMessage("User Name / Email does not match the logged-in user.");
      return;
    }

    if (resetData.newPassword !== resetData.newPasswordRetype) {
      setErrorMessage("New passwords do not match.");
      return;
    }

    const isStrong = Object.values(criteria).every(Boolean);
    if (!isStrong) {
      setErrorMessage("Password does not meet all the strength requirements.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await putApi({
        apiUrl: `/api/emp/auth/reset-password/${user._id}`,
        data: {
          currentPassword: resetData.currentPassword,
          newPassword: resetData.newPassword,
          newPasswordRetype: resetData.newPasswordRetype,
        },
      });

      if (response.success) {
        setShowSuccessPopup(true);
      } else {
        setErrorMessage(response.message || "Failed to reset password");
      }
    } catch (error) {
      setErrorMessage(error.message || "Failed to reset password.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex justify-center items-start sm:items-center bg-black bg-opacity-40 p-4 overflow-y-auto scrollbar-hide">
      <div className="my-auto bg-white rounded-3xl shadow-2xl flex overflow-hidden w-full max-w-[800px] h-auto md:max-h-[calc(100vh-2rem)] relative">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition p-2 hover:bg-gray-100 rounded-full z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Left Column (Illustration & Info) */}
        <div className="hidden md:flex md:w-[300px] bg-[#F7FAF9] pt-6 px-6 pb-12 flex-col justify-between border-r border-gray-100 select-none">
          {/* Logo & Tagline */}
          <div className="flex justify-center w-full ">
            <img src={Logo} className="h-16 w-auto" alt="Logo" />
          </div>

          {/* Shield Illustration & Text Group */}
          <div className="my-auto flex flex-col items-center justify-center py-1 w-full">
            <div className="flex justify-center w-full ">
              <img
                src={ResetImg}
                className="w-36 h-auto"
                alt="Reset Password"
              />
            </div>
            <div className="max-w-[240px] mx-auto w-full text-left mt-3">
              <h3 className="text-[13.5px] font-bold text-[#06513D]">
                Secure Your Account
              </h3>
              <p className="text-[12px] text-gray-500 mt-1.5 leading-relaxed">
                For your security, please create <br />a new password to
                continue <br />
                accessing CARBURN.
              </p>
            </div>
          </div>

          {/* Security Checklist Bullet Points */}
          <div className="space-y-3.5 max-w-[240px] mx-auto w-full">
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[#06513D] shrink-0 mt-0.5">
                <img
                  src={FirstImg}
                  className="w-6 h-6 object-contain"
                  alt="Safe"
                />
              </div>
              <div>
                <h4 className="text-[13px] font-bold text-[#06513D]">
                  Your data is safe
                </h4>
                <p className="text-[11.5px] text-gray-500 mt-0.5 leading-relaxed">
                  We use enterprise-grade security to protect your information.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[#06513D] shrink-0 mt-0.5">
                <img
                  src={SecondImg}
                  className="w-6 h-6 object-contain"
                  alt="Secure"
                />
              </div>
              <div>
                <h4 className="text-[13px] font-bold text-[#06513D]">
                  Encrypted & Protected
                </h4>
                <p className="text-[11.5px] text-gray-500 mt-0.5 leading-relaxed">
                  All data is encrypted in transit and at rest.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-lg flex items-center text-bold justify-center text-[#06513D] shrink-0 mt-0.5">
                <img
                  src={ThirdImg}
                  className="w-6 h-6 object-contain"
                  alt="Control"
                />
              </div>
              <div>
                <h4 className="text-[13px] font-bold text-[#06513D]">
                  You're in Control
                </h4>
                <p className="text-[11.5px] text-gray-500 mt-0.5 leading-relaxed">
                  Only you can access your account and update your password.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column (Form & Fields) */}
        <div className="flex-1 bg-white md:w-[600px] p-6 sm:p-8 flex flex-col justify-start md:overflow-y-auto scrollbar-hide min-h-0">
          {/* Top circular Lock Icon */}
          <div className="w-12 h-12 bg-[#EBF5F2] text-[#06513D] rounded-full flex items-center justify-center mx-auto mb-2 border border-emerald-50 shadow-sm shrink-0 self-center">
            <img
              src={SecondImg}
              className="w-6 h-6 object-contain"
              alt="Lock"
            />
          </div>

          <h2 className="text-[21px] font-bold text-gray-800 text-center tracking-tight">
            Create New Password
          </h2>
          <p className="text-[12px] text-gray-500 text-center max-w-xs mx-auto mt-1.5 mb-5 leading-relaxed">
            You are using a temporary password. <br />
            Please create a new strong password to secure your account.
          </p>

          <form className="space-y-5 pb-4" onSubmit={handleSubmit}>
            {/* If tempPassword is not available, show User Name and Temp Password inputs */}
            {/* {showTempPasswordInput && (
              <>
                <div>
                  <label className="block text-[10.5px] font-bold text-gray-700 mb-1 uppercase tracking-wider">
                    User Name / Email
                  </label>
                  <div className="border border-gray-300 rounded-lg flex items-center px-3 py-2 bg-gray-50 focus-within:border-[#06513D] focus-within:bg-white focus-within:ring-1 focus-within:ring-[#06513D] transition">
                    <User className="w-4 h-4 text-gray-400 mr-2" />
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="Enter your email"
                      value={resetData.email}
                      onChange={handleChange}
                      className="w-full bg-transparent outline-none text-xs placeholder-gray-400 text-gray-800"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10.5px] font-bold text-gray-700 mb-1 uppercase tracking-wider">
                    Temporary Password
                  </label>
                  <div className="border border-gray-300 rounded-lg flex items-center px-3 py-2 bg-gray-50 focus-within:border-[#06513D] focus-within:bg-white focus-within:ring-1 focus-within:ring-[#06513D] transition">
                    <Lock className="w-4 h-4 text-gray-400 mr-2" />
                    <input
                      name="currentPassword"
                      type="text"
                      required
                      placeholder="Enter temporary password"
                      value={resetData.currentPassword}
                      onChange={handleChange}
                      className="w-full bg-transparent outline-none text-xs placeholder-gray-400 text-gray-800"
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </>
            )} */}

            {/* New Password */}
            <div>
              <label className="block text-[13px] font-bold text-gray-700 mb-2 ">
                New Password
              </label>
              <div className="border border-gray-300 rounded-lg flex items-center px-3 py-2 bg-white focus-within:border-[#06513D] focus-within:ring-1 focus-within:ring-[#06513D] transition">
                <Lock className="w-4 h-4 text-gray-400 mr-2" />
                <input
                  name="newPassword"
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="Enter new password"
                  value={resetData.newPassword}
                  onChange={handleChange}
                  className="w-full bg-transparent outline-none text-xs placeholder-gray-400 text-gray-800"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 focus:outline-none ml-2 shrink-0"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>

              {/* Password Strength progress bar */}
              {resetData.newPassword && (
                <div className="flex items-center justify-between mt-1.5 flex-wrap gap-2">
                  <div className="flex gap-1 flex-1 min-w-[120px] max-w-[180px]">
                    {[1, 2, 3, 4].map((index) => (
                      <div
                        key={index}
                        className={`h-[5px] flex-1 rounded-full ${
                          index <= strength.bars
                            ? strength.barColor
                            : "bg-gray-200"
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-[11px] text-gray-500 font-medium">
                    Password Strength:{" "}
                    <span className={`${strength.color} font-bold`}>
                      {strength.text}
                    </span>
                  </span>
                </div>
              )}
            </div>

            {/* Retype Password */}
            <div>
              <label className="block text-[13px] font-bold text-gray-700 mb-2 ">
                Retype New Password
              </label>
              <div className="border border-gray-300 rounded-lg flex items-center px-3 py-2 bg-white focus-within:border-[#06513D] focus-within:ring-1 focus-within:ring-[#06513D] transition">
                <Lock className="w-4 h-4 text-gray-400 mr-2" />
                <input
                  name="newPasswordRetype"
                  type={showRetypePassword ? "text" : "password"}
                  required
                  placeholder="Retype new password"
                  value={resetData.newPasswordRetype}
                  onChange={handleChange}
                  className="w-full bg-transparent outline-none text-xs placeholder-gray-400 text-gray-800"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 focus:outline-none ml-2 shrink-0"
                  onClick={() => setShowRetypePassword(!showRetypePassword)}
                  disabled={isLoading}
                >
                  {showRetypePassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Verification Criteria checklist */}
            <div className="bg-[#F4F8F6] rounded-xl p-3 mt-4 border border-emerald-50/50">
              <span className="block text-[12px] font-bold text-[#06513D] mb-2">
                Password must contain:
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-[12px] text-gray-600">
                <div className="flex items-center gap-2">
                  <div
                    className={`w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0 transition-colors duration-300 ${
                      criteria.length
                        ? "bg-[#06513D] text-white"
                        : "bg-gray-200 text-gray-400"
                    }`}
                  >
                    <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                  </div>
                  <span>At least 8 characters</span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className={`w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0 transition-colors duration-300 ${
                      criteria.lowercase
                        ? "bg-[#06513D] text-white"
                        : "bg-gray-200 text-gray-400"
                    }`}
                  >
                    <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                  </div>
                  <span>One lowercase letter (a-z)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className={`w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0 transition-colors duration-300 ${
                      criteria.uppercase
                        ? "bg-[#06513D] text-white"
                        : "bg-gray-200 text-gray-400"
                    }`}
                  >
                    <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                  </div>
                  <span>One uppercase letter (A-Z)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className={`w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0 transition-colors duration-300 ${
                      criteria.special
                        ? "bg-[#06513D] text-white"
                        : "bg-gray-200 text-gray-400"
                    }`}
                  >
                    <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                  </div>
                  <span>One special character (!@#$%^&*)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className={`w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0 transition-colors duration-300 ${
                      criteria.number
                        ? "bg-[#06513D] text-white"
                        : "bg-gray-200 text-gray-400"
                    }`}
                  >
                    <Check className="w-2.5 h-2.5" strokeWidth={3.5} />
                  </div>
                  <span>One number (0-9)</span>
                </div>
              </div>
            </div>

            {/* Information Banner */}
            <div className="bg-[#EAF2FC] border border-[#B8D5F8] text-[#1D5494] rounded-xl p-2.5 flex items-start gap-2 mt-3 text-[10.5px] leading-relaxed">
              <Info className="w-3.5 h-3.5 shrink-0 text-[#1D5494] mt-0.5" />
              <span>
                Once you update your password, you will be signed in to{" "}
                <br></br> your account automatically.
              </span>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#06513D] hover:bg-[#054a36] text-white py-2.5 px-4 rounded-xl font-bold mt-4 flex items-center justify-center gap-2 transition-all duration-300 shadow-md hover:shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed text-[13.5px]"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving Password...
                </div>
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4" />
                  Update Password & Sign In
                </>
              )}
            </button>
          </form>
        </div>

        {/* Success Popup Overlay */}
        {showSuccessPopup && (
          <div className="absolute inset-0 z-[100] flex justify-center items-center bg-black bg-opacity-40 p-4">
            <PopupCard
              type="success"
              title="Success"
              message="Password changed successfully"
            />
          </div>
        )}

        {/* Error Popup Overlay */}
        {errorMessage && (
          <div className="absolute inset-0 z-[100] flex justify-center items-center bg-black bg-opacity-40 p-4">
            <PopupCard type="error" title="Error" message={errorMessage} />
          </div>
        )}
      </div>
    </div>
  );
}
